import { useMutation } from '@tanstack/react-query'
import { AlertDialog, Box, Button, Flex, Text } from '@radix-ui/themes'
import { ReloadIcon } from '@radix-ui/react-icons'
import { useTRPC } from '~/utils/trpc'
import { MessageCallout } from '~/components/MessageCallout'
import type { StationSectionProps } from '../types'

export const RebuildStationIndexButton = ({ station }: StationSectionProps) => {
  const trpc = useTRPC()
  const rebuild = useMutation(trpc.ops.stations.invalidateStationIndex.mutationOptions())

  return (
    <AlertDialog.Root
      onOpenChange={(open) => {
        if (open) rebuild.reset()
      }}
    >
      <AlertDialog.Trigger>
        <Button size="1" variant="soft" color="gray">
          <ReloadIcon />
          Rebuild index
        </Button>
      </AlertDialog.Trigger>
      <AlertDialog.Content maxWidth="460px">
        <AlertDialog.Title>Rebuild the index of {station.name}?</AlertDialog.Title>
        <AlertDialog.Description size="2">
          Deletes this station&apos;s stored index snapshots and current value. The next warm tick, within 30
          minutes, rebuilds the current value; until then, and for dates before the rebuild, the index is computed
          live from the anchor reading.
        </AlertDialog.Description>
        {rebuild.isSuccess ? (
          <Text color="green" size="2" as="p" mt="3">
            Index cleared. It rebuilds on the next warm tick.
          </Text>
        ) : null}
        {rebuild.error ? (
          <Box mt="3">
            <MessageCallout color="red" message={rebuild.error.message} />
          </Box>
        ) : null}
        <Flex gap="3" mt="4" justify="end">
          <AlertDialog.Cancel>
            <Button variant="soft" color="gray" disabled={rebuild.isPending}>
              {rebuild.isSuccess ? 'Close' : 'Cancel'}
            </Button>
          </AlertDialog.Cancel>
          {rebuild.isSuccess ? null : (
            <Button loading={rebuild.isPending} onClick={() => rebuild.mutate({ stationId: station.id })}>
              Rebuild
            </Button>
          )}
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  )
}
